import { ICellRendererParams } from "@ag-grid-community/core";
import { Tooltip, useMergeRefs, usePrevious } from "@chakra-ui/react";
import { FundFamilyUseAllocationTableAmountTypeDef } from "gen/model/fundFamilyUseAllocationTableAmountTypeDef";
import { FundFamilyUseAllocationTableRow } from "gen/model/fundFamilyUseAllocationTableRow";
import { first } from "lodash-es";
import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  FundFamilyUseAllocationTableAmountInfoIcon,
  FundFamilyUseAllocationTableAmountInfoTooltip,
} from "shared/components/fund-family-use-allocation/FundFamilyUseAllocationTableAmountInfo";
import { FundFamilyUseAllocationTableContext } from "shared/components/fund-family-use-allocation/FundFamilyUseAllocationTableProps";
import {
  FundFamilyUseAllocationTableAmountColHeaderComponentParams,
  FundFamilyUseAllocationTableFundFamilyOrFundDef,
} from "shared/components/fund-family-use-allocation/FundFamilyUseAllocationTableTypes";
import { isFundFamilyUseAllocationTableRowEditable } from "shared/components/fund-family-use-allocation/FundFamilyUseAllocationTableUtils";
import { FundFamilyUseAllocationTableAmountFrontend } from "shared/components/fund-family-use-allocation/types";
import { SimpleMoneyInput } from "shared/components/input/text/SimpleMoneyInput";
import { getMaybernTableCellError } from "shared/components/table/maybern/MaybernTableErrors";
import { MaybernTableFocusCellRendererWrapper } from "shared/components/table/maybern/MaybernTableFocusCellRendererWrapper";
import { formatPercentage } from "shared/utils/formattingUtils";
import invariant from "shared/utils/TinyInvariant";

export interface FundFamilyUseAllocationTableAmountCellRendererProps
  extends ICellRendererParams<
    FundFamilyUseAllocationTableRow,
    FundFamilyUseAllocationTableAmountFrontend
  > {
  fundFamilyOrFundDef: FundFamilyUseAllocationTableFundFamilyOrFundDef;
  amountTypeDef: FundFamilyUseAllocationTableAmountTypeDef;
  forceReadOnly?: boolean;
  showIcon?: boolean;
}

export const FundFamilyUseAllocationTableAmountCellRenderer = React.forwardRef<
  HTMLInputElement,
  FundFamilyUseAllocationTableAmountCellRendererProps
>(
  (
    {
      amountTypeDef,
      fundFamilyOrFundDef,
      forceReadOnly = false,
      showIcon = true,
      ...params
    },
    ref,
  ) => {
    const { t } = useTranslation(["common"]);

    const localRef = useRef<HTMLInputElement>(null);
    const mergedRef = useMergeRefs(ref, localRef);

    const { data, node, column, context } = params;
    const { is_read_only: isDefReadOnly } = amountTypeDef;
    const { tableDef, getAllocation }: FundFamilyUseAllocationTableContext =
      context;
    invariant(data);

    const category = tableDef.categories.find((c) => c.id === data.category_id);

    const isFundFamily = !("fund_id" in fundFamilyOrFundDef);

    const isReadOnly =
      forceReadOnly ||
      isDefReadOnly ||
      (category?.allow_auto_allocate === false && isFundFamily) ||
      (category?.allow_auto_allocate &&
        ((isFundFamily && !data.is_auto_allocate) ||
          (!isFundFamily && data.is_auto_allocate)));

    const prevReadOnly = usePrevious(isReadOnly);

    const isFundFamilyOrFirstFund =
      isFundFamily ||
      first(tableDef.funds)?.fund_id === fundFamilyOrFundDef.fund_id;

    const headerComponentParams: FundFamilyUseAllocationTableAmountColHeaderComponentParams =
      params.column?.getColDef().headerComponentParams;

    const isFirstAmount =
      headerComponentParams.amountTypeDef.id === tableDef.amount_types[0].id;

    const currentAllocation =
      showIcon && data.is_auto_allocate && "fund_id" in fundFamilyOrFundDef
        ? getAllocation?.({
            context,
            row: data,
            amountTypeDef,
            fundDef: fundFamilyOrFundDef,
          })
        : undefined;

    // keep old allocation around to prevent flickering while loading
    const allocationRef = useRef(currentAllocation);
    if (currentAllocation) {
      allocationRef.current = currentAllocation;
    }
    const allocation = allocationRef.current;

    const inputElement = (
      <SimpleMoneyInput
        ref={mergedRef}
        variant="simple-table"
        value={params.value?.amount}
        placeholder={isReadOnly ? undefined : t("common:enterAmount")}
        isReadOnly={isReadOnly}
        allowNegative={true}
        showCurrencyIcon={false}
        showCurrencyPrefix={false}
        isDisabled={!isFundFamilyUseAllocationTableRowEditable(params.node)}
        error={getMaybernTableCellError(params)}
        onValueChange={({ value }) => {
          if (!isReadOnly) {
            params.setValue?.({
              ...params.value,
              amount: value,
            });
          }
        }}
        inputLeftElement={
          showIcon &&
          data.is_auto_allocate &&
          allocation?.allocationRuleId &&
          allocation.allocationRuleName ? (
            <FundFamilyUseAllocationTableAmountInfoIcon
              fundFamilyId={tableDef.fund_family.fund_family_id}
              allocationRuleId={allocation.allocationRuleId}
              allocationRuleName={allocation.allocationRuleName}
              isDisabled={!currentAllocation}
            />
          ) : undefined
        }
      />
    );

    useEffect(() => {
      if (
        node.rowIndex !== null &&
        column &&
        prevReadOnly &&
        !isReadOnly &&
        isFundFamilyOrFirstFund &&
        isFirstAmount
      ) {
        params.api.setFocusedCell(node.rowIndex, column);
      }
    }, [
      prevReadOnly,
      isReadOnly,
      isFundFamilyOrFirstFund,
      isFirstAmount,
      column,
      node.rowIndex,
      params.api,
    ]);

    if (
      // fund is not applicable to this row
      !isFundFamily &&
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      !data.funds[fundFamilyOrFundDef.fund_id]
    ) {
      return undefined;
    }

    return (
      <MaybernTableFocusCellRendererWrapper params={params} inputRef={localRef}>
        {currentAllocation?.ownershipPercentage ? (
          <Tooltip
            label={
              <FundFamilyUseAllocationTableAmountInfoTooltip>
                {formatPercentage(
                  currentAllocation.ownershipPercentage.mul(100),
                )}
              </FundFamilyUseAllocationTableAmountInfoTooltip>
            }
            hasArrow={true}
            openDelay={0}
            closeDelay={0}
          >
            {inputElement}
          </Tooltip>
        ) : (
          inputElement
        )}
      </MaybernTableFocusCellRendererWrapper>
    );
  },
);

FundFamilyUseAllocationTableAmountCellRenderer.displayName =
  "AllocationAmountCellRenderer";
